import React, { useCallback } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { RovodalCanvasKeys } from '@atlassian/rovodal-context/canvas-keys';
import { useRovodal } from '@atlassian/rovodal-context/store';
import { RovodalStoreKeys } from '@atlassian/rovodal-context/store-keys';

import JiraAdminAgent from '../../common/assets/jira-admin-agent.svg';
import { useConvertActionsToPlan } from '../../hooks/useConvertActionsToPlan';

import { JiraAdminAgentActionPlanCard } from './action-cards/JiraAdminAgentActionPlanCard';

const invalidPayloadMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.JiraAdminAgentRenderer.invalidPayload.non-final',
	defaultMessage: "We couldn't load the response. Wait a few minutes or try again.",
	description:
		'Error message shown when a server response is invalid, and the UI cannot be rendererd',
});

export const JiraAdminAgentRenderer = (
	props: ActionRendererFor<typeof ActionKeys.JiraAdminAgent>,
): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [entry, { open, close }] = useRovodal(RovodalStoreKeys.jiraAdminAgentCanvas);
	const isCanvasOpen = entry?.isOpen ?? false;
	const isCanvasLoading = isCanvasOpen && !(entry?.state?.loaded ?? false);

	const {
		messageId,
		conversationId,
		recipientAgentId,
		isHistoricalMessage,
		isLastMessage,
		cloudId,
		productKey,
		actions,
	} = props;
	const convertedPlan = useConvertActionsToPlan({ actions });

	const openCanvas = useCallback(() => {
		open({
			canvasKey: RovodalCanvasKeys.jiraAdminAgentBlastRadiusCanvas,
			fullSizeModal: true,
			title: convertedPlan.topLevelPlan?.payload.title,
			actions,
			messageId,
			conversationId,
			recipientAgentId,
			isHistoricalMessage,
			isLastMessage,
			cloudId,
			productKey,
			sendMessageConfirmedTools: props.sendMessageConfirmedTools,
			readOnlyAgentHeader: {
				name: 'Jira Admin Agent',
				iconSrc: JiraAdminAgent,
			},
			chatOverrides: {
				agentsConfig: {
					defaultAgentId: 'jira-admin-agent',
					filter: ['jira-admin-agent'],
				},
				uiConfig: {
					externalEmbed: true,
					fullScreen: false,
					isContentPreviewSupported: true,
					elements: {
						mainChatHeader: false,
						browseAgentsButton: false,
						closeButton: false,
						fullscreenButton: false,
						newChatButton: false,
						createAgentButton: false,
						agentsMenu: false,
						navbar: false,
						stagingAreaEnabled: false,
						voiceDictationButton: false,
						chatModeMenuButton: false,
						fileUploadButton: false,
						forceFullscreenHeader: false,
					},
				},
			},
		});
	}, [
		open,
		messageId,
		conversationId,
		recipientAgentId,
		isHistoricalMessage,
		isLastMessage,
		cloudId,
		productKey,
		actions,
		convertedPlan.topLevelPlan?.payload.title,
		props.sendMessageConfirmedTools,
	]);

	const onDismissed = useCallback(() => {
		// Dismissing the plan should not leave a stale review canvas open.
		if (isCanvasOpen) {
			close();
		}
	}, [close, isCanvasOpen]);

	return (
		<>
			{convertedPlan.topLevelPlan ? (
				<JiraAdminAgentActionPlanCard
					{...props}
					convertedPlan={convertedPlan}
					openCanvas={openCanvas}
					onDismissed={onDismissed}
					isCanvasOpen={isCanvasOpen}
					isCanvasLoading={isCanvasLoading}
				/>
			) : (
				<Inline space="space.100" alignBlock="center">
					<StatusErrorIcon color={token('color.icon.danger')} label="" />
					<Text>{formatMessage(invalidPayloadMessage)}</Text>
				</Inline>
			)}
		</>
	);
};
